import React, { useEffect, useMemo, useState } from 'react';
import api from '../../services/api';

const money = (v) => {
  const n = Number(v || 0);
  return `$${n.toFixed(4)}`;
};

const AIUsageMonitoring = ({ adminUser, onPageChange }) => {
  const [period, setPeriod] = useState('7d');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [dashboard, setDashboard] = useState(null);
  const [quick, setQuick] = useState(null);
  const [health, setHealth] = useState(null);

  useEffect(() => {
    onPageChange?.('ai-usage');
  }, [onPageChange]);

  const loadAll = async () => {
    try {
      setError(null);
      setLoading(true);

      const [dashRes, quickRes, healthRes] = await Promise.all([
        api.get('/admin/ai-usage', { params: { period } }),
        api.get('/admin/ai-usage/stats'),
        api.get('/admin/ai-usage/health'),
      ]);

      setDashboard(dashRes.data);
      setQuick(quickRes.data);
      setHealth(healthRes.data);
    } catch (e) {
      console.error('❌ AIUsageMonitoring load error:', e);
      setError(e?.response?.data?.message || e.message || 'Erreur chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  const stats = dashboard?.ai_usage?.stats;
  const costs = dashboard?.ai_usage?.costs;
  const topUsers = dashboard?.ai_usage?.top_users || [];
  const features = dashboard?.ai_usage?.features || [];
  const trends = dashboard?.ai_usage?.trends || [];
  const projection = dashboard?.ai_usage?.projection;

  const sourceLabel = stats?.source === 'ai_usage_table'
    ? '✅ Tracking précis (table ai_usage)'
    : '⚠️ Fallback (messages.openai_tokens)';

  const exportCSV = async () => {
    try {
      const days = period === '24h' ? 1 : period === '7d' ? 7 : period === '30d' ? 30 : 90;
      const res = await api.get('/admin/ai-usage/export', {
        params: { days },
        responseType: 'blob'
      });

      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ai_usage_${days}d_${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('❌ export error:', e);
      alert('Erreur export CSV');
    }
  };

  const bg = { background: '#0f172a', color: 'white', minHeight: 'calc(100vh - 64px)', padding: '2rem' };
  const card = { background: '#1e293b', borderRadius: '1rem', padding: '1.25rem' };
  const subtle = { color: '#94a3b8' };

  if (loading) {
    return (
      <div style={bg}>
        <div style={card}>
          <h2>⏳ Chargement dashboard IA...</h2>
          <p style={subtle}>Connexion au backend admin/ai-usage</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={bg}>
        <div style={{ ...card, border: '1px solid rgba(239,68,68,.35)' }}>
          <h2>❌ Erreur</h2>
          <p style={subtle}>{error}</p>
          <button onClick={loadAll} style={{ marginTop: 12, padding: '10px 14px', borderRadius: 10, cursor: 'pointer' }}>
            🔄 Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={bg}>
      <div style={{ ...card, marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ margin: 0 }}>🤖 Monitoring IA & Coûts</h1>
            <div style={subtle}>
              {sourceLabel} • Admin: {adminUser?.email || adminUser?.username || '—'}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              style={{ padding: '10px 12px', borderRadius: 10 }}
            >
              <option value="24h">24h</option>
              <option value="7d">7 jours</option>
              <option value="30d">30 jours</option>
              <option value="90d">90 jours</option>
            </select>

            <button onClick={loadAll} style={{ padding: '10px 14px', borderRadius: 10, cursor: 'pointer' }}>
              🔄 Actualiser
            </button>

            <button onClick={exportCSV} style={{ padding: '10px 14px', borderRadius: 10, cursor: 'pointer' }}>
              ⬇️ Export CSV
            </button>
          </div>
        </div>
      </div>

      {/* Quick stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 16 }}>
        <div style={card}>
          <div style={subtle}>Aujourd’hui</div>
          <div style={{ fontSize: 22, fontWeight: 700 }}>{quick?.today?.requests || 0} req</div>
          <div style={subtle}>{quick?.today?.tokens || 0} tokens • {money(quick?.today?.cost_usd)}</div>
        </div>
        <div style={card}>
          <div style={subtle}>Ce mois</div>
          <div style={{ fontSize: 22, fontWeight: 700 }}>{quick?.month?.requests || 0} req</div>
          <div style={subtle}>{quick?.month?.tokens || 0} tokens • {money(quick?.month?.cost_usd)}</div>
        </div>
        <div style={card}>
          <div style={subtle}>État OpenAI</div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>
            {health?.openai_health?.status || 'unknown'}
          </div>
          <div style={subtle}>
            reachable: {health?.openai_health?.api_reachable ? 'yes' : 'no'} • {health?.openai_health?.response_time_ms ?? '—'} ms
          </div>
        </div>
      </div>

      {/* Main stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12, marginBottom: 16 }}>
        <div style={card}>
          <div style={subtle}>Total requêtes ({period})</div>
          <div style={{ fontSize: 26, fontWeight: 800 }}>{stats?.total_requests ?? 0}</div>
          <div style={subtle}>Users uniques: {stats?.unique_users ?? 0}</div>
        </div>
        <div style={card}>
          <div style={subtle}>Total tokens ({period})</div>
          <div style={{ fontSize: 26, fontWeight: 800 }}>{stats?.total_tokens ?? 0}</div>
          <div style={subtle}>Avg / req: {stats?.avg_tokens_per_request ?? 0}</div>
        </div>
        <div style={card}>
          <div style={subtle}>Coût estimé ({period})</div>
          <div style={{ fontSize: 26, fontWeight: 800 }}>{money(costs?.total_cost_usd)}</div>
          <div style={subtle}>Avg / req: {money(costs?.avg_cost_per_request)}</div>
        </div>
        <div style={card}>
          <div style={subtle}>Projection</div>
          <div style={{ fontSize: 18, fontWeight: 800 }}>
            Mensuel: ${Number(projection?.monthly_projection || 0).toFixed(2)}
          </div>
          <div style={subtle}>
            Jour: {money(projection?.daily_average)} • Annuel: ${Number(projection?.yearly_projection || 0).toFixed(2)}
          </div>
        </div>
      </div>

      {/* Top users + Features */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: 12, marginBottom: 16 }}>
        <div style={card}>
          <h3 style={{ marginTop: 0 }}>👑 Top utilisateurs (coût)</h3>
          {topUsers.length === 0 ? (
            <p style={subtle}>Aucune donnée (vérifie que tu trackes dans ai_usage).</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: '#cbd5e1' }}>
                    <th style={{ padding: 8 }}>Email</th>
                    <th style={{ padding: 8 }}>Req</th>
                    <th style={{ padding: 8 }}>Tokens</th>
                    <th style={{ padding: 8 }}>Coût</th>
                  </tr>
                </thead>
                <tbody>
                  {topUsers.map(u => (
                    <tr key={u.user_id} style={{ borderTop: '1px solid rgba(148,163,184,.2)' }}>
                      <td style={{ padding: 8 }}>{u.email}</td>
                      <td style={{ padding: 8 }}>{u.requests}</td>
                      <td style={{ padding: 8 }}>{u.tokens}</td>
                      <td style={{ padding: 8 }}>{money(u.cost_usd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div style={card}>
          <h3 style={{ marginTop: 0 }}>🧩 Usage par fonctionnalité</h3>
          {features.length === 0 ? (
            <p style={subtle}>Aucune donnée (table ai_usage vide ou fallback messages).</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: '#cbd5e1' }}>
                    <th style={{ padding: 8 }}>Feature</th>
                    <th style={{ padding: 8 }}>Req</th>
                    <th style={{ padding: 8 }}>Tokens</th>
                    <th style={{ padding: 8 }}>Coût</th>
                  </tr>
                </thead>
                <tbody>
                  {features.map(f => (
                    <tr key={f.feature} style={{ borderTop: '1px solid rgba(148,163,184,.2)' }}>
                      <td style={{ padding: 8 }}>{f.feature}</td>
                      <td style={{ padding: 8 }}>{f.usage_count}</td>
                      <td style={{ padding: 8 }}>{f.tokens}</td>
                      <td style={{ padding: 8 }}>{money(f.cost_usd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Trends */}
      <div style={card}>
        <h3 style={{ marginTop: 0 }}>📈 Tendances (par jour)</h3>
        {trends.length === 0 ? (
          <p style={subtle}>Pas assez de données pour tracer les tendances.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: '#cbd5e1' }}>
                  <th style={{ padding: 8 }}>Date</th>
                  <th style={{ padding: 8 }}>Req</th>
                  <th style={{ padding: 8 }}>Tokens</th>
                  <th style={{ padding: 8 }}>Coût</th>
                </tr>
              </thead>
              <tbody>
                {trends.map(t => (
                  <tr key={t.date} style={{ borderTop: '1px solid rgba(148,163,184,.2)' }}>
                    <td style={{ padding: 8 }}>{t.date}</td>
                    <td style={{ padding: 8 }}>{t.requests}</td>
                    <td style={{ padding: 8 }}>{t.tokens}</td>
                    <td style={{ padding: 8 }}>{money(t.cost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div style={{ marginTop: 10, color: '#94a3b8', fontSize: 13 }}>
          Astuce: si tu veux des graphs, tu peux plug Recharts/Chart.js plus tard.
        </div>
      </div>
    </div>
  );
};

export default AIUsageMonitoring;
